import { computed, ref } from 'vue';
import { addManager, listManagers, removeManager } from '@/js/core/utils/trays-api';
import { useUserStore } from '@/js/core/stores/user-store';

/**
 * Manager list state for the tray edit form
 *
 * Manager mutations are deferred: nothing reaches the API until runManagerSaves() is called
 * by the combined save.
 *
 * @param getTray - Getter returning the current tray
 * @returns {object} Manager edit state and actions
 */
export function useTrayManagerEdit(getTray) {
  const userStore = useUserStore();

  const managers = ref([]);
  const pendingAdds = ref([]);
  const pendingRemoves = ref([]);
  const saveErrors = ref([]);
  const loading = ref(false);
  const error = ref('');
  const isInitialised = ref(false);

  const hasPendingChanges = computed(
    () => pendingAdds.value.length > 0 || pendingRemoves.value.length > 0
  );

  const excludedManagerUuids = computed(() => [
    getTray().owner_uuid,
    ...managers.value.map(m => m.user_uuid),
    ...pendingAdds.value.map(u => u.uuid),
  ]);

  const isOwner = () => getTray().is_owner;

  const isSelf = manager => manager.user_uuid === userStore.uuid;

  const fetchManagers = async () => {
    loading.value = true;

    try {
      const result = await listManagers(getTray().uuid);
      managers.value = result.data || [];
    } catch (e) {
      error.value = 'Failed to load managers';
    } finally {
      loading.value = false;
      isInitialised.value = true;
    }
  };

  const handleAddManager = user => {
    if (excludedManagerUuids.value.includes(user.uuid)) {
      return;
    }

    pendingAdds.value.push({ uuid: user.uuid, name: user.name, email: user.email });
  };

  const handleRemoveManager = manager => {
    pendingRemoves.value.push(manager.uuid);
  };

  /**
   * Commit every pending manager change, one call at a time
   *
   * @returns {Promise<{allSucceeded: boolean, selfRemoved: boolean}>}
   */
  const runManagerSaves = async () => {
    const trayUuid = getTray().uuid;
    saveErrors.value = [];

    // pendingRemoves holds tray_user RECORD uuids, but identity is carried on user_uuid.
    // Capture the mapping before the removals land, because a successful pass refetches
    // the list and the records we need to resolve are gone by then.
    const userUuidByRecordUuid = new Map(managers.value.map(m => [m.uuid, m.user_uuid]));
    const removedUserUuids = [];

    for (const user of [...pendingAdds.value]) {
      try {
        await addManager(trayUuid, user.uuid);
        pendingAdds.value = pendingAdds.value.filter(u => u.uuid !== user.uuid);
      } catch (e) {
        saveErrors.value.push(`Failed to add ${user.name}: ${e.message}`);
      }
    }

    for (const managerUuid of [...pendingRemoves.value]) {
      try {
        await removeManager(trayUuid, managerUuid);
        pendingRemoves.value = pendingRemoves.value.filter(u => u !== managerUuid);
        removedUserUuids.push(userUuidByRecordUuid.get(managerUuid));
      } catch (e) {
        const mgr = managers.value.find(m => m.uuid === managerUuid);
        saveErrors.value.push(`Failed to remove ${mgr?.user_name || 'manager'}: ${e.message}`);
      }
    }

    const allSucceeded = pendingAdds.value.length === 0 && pendingRemoves.value.length === 0;

    // An owner stays in the tray after dropping their manager row, so only a non-owner
    // is actually losing access here.
    const selfRemoved = !isOwner() && removedUserUuids.includes(userStore.uuid);

    if (!selfRemoved && !allSucceeded) {
      await fetchManagers();
    }

    return { allSucceeded, selfRemoved };
  };

  return {
    managers,
    pendingAdds,
    pendingRemoves,
    saveErrors,
    loading,
    error,
    isInitialised,
    hasPendingChanges,
    excludedManagerUuids,
    isOwner,
    isSelf,
    handleAddManager,
    handleRemoveManager,
    fetchManagers,
    runManagerSaves,
  };
}
